import { createHttpClient } from '@adapters/HttpClient';
import { CouriersApiAdapter } from '@modules/Orders/List/adapters/CouriersApiAdapter';
import { PaymentMethodsApiAdapter } from '@modules/Orders/List/adapters/PaymentMethodsApiAdapter';
import { ShopsApiAdapter } from '@modules/Orders/List/adapters/ShopsApiAdapter';
import { TeamsApiAdapter } from '@modules/Orders/List/adapters/TeamsApiAdapter';
import { useCourierServices } from '@modules/Rates/hooks/useCourierServices';
import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { CountriesApiAdapter } from '@shared/adapters/CountriesApiAdapter';
import { Separator } from '@shared/components/ui/shadcn/separator';
import { apiClientWH } from '@shared/services';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export interface LogisticPivotFiltersBarState {
  startDate?: string;
  endDate?: string;
  teamId?: number | null;
  paymentMethodId?: number | null;
  shopId?: number | null;
  courierId?: number | null;
  countryId?: number | null;
  courierServiceId?: number | null;
}

interface LogisticPivotFiltersPanelProps {
  startDate?: string;
  endDate?: string;
  teamId?: number | null;
  paymentMethodId?: number | null;
  shopId?: number | null;
  courierId?: number | null;
  countryId?: number | null;
  courierServiceId?: number | null;
  onFiltersChange: (filters: LogisticPivotFiltersBarState) => void;
  getDefaultStartDate: () => string;
  getDefaultEndDate: () => string;
  /** Solo `tenant_3pl`: filtro por cliente (team). */
  showTeamFilter?: boolean;
}

/** Formulario de filtros para usar dentro de `FiltersDropdown` (Orders/List). */
export const LogisticPivotFiltersPanel = ({
  startDate,
  endDate,
  teamId,
  paymentMethodId,
  shopId,
  courierId,
  countryId,
  courierServiceId,
  onFiltersChange,
  getDefaultStartDate,
  getDefaultEndDate,
  showTeamFilter = true,
}: LogisticPivotFiltersPanelProps) => {
  const { t } = useTranslation('logisticPivotModule');

  const [localStartDate, setLocalStartDate] = useState<string | undefined>(startDate);
  const [localEndDate, setLocalEndDate] = useState<string | undefined>(endDate);
  const [localTeamId, setLocalTeamId] = useState<string>(teamId != null ? String(teamId) : '');
  const [localPaymentMethodId, setLocalPaymentMethodId] = useState<string>(
    paymentMethodId != null ? String(paymentMethodId) : ''
  );
  const [localShopId, setLocalShopId] = useState<string>(shopId != null ? String(shopId) : '');
  const [localCourierId, setLocalCourierId] = useState<string>(courierId != null ? String(courierId) : '');
  const [localCountryId, setLocalCountryId] = useState<string>(countryId != null ? String(countryId) : '');
  const [localCourierServiceId, setLocalCourierServiceId] = useState<string>(
    courierServiceId != null ? String(courierServiceId) : ''
  );

  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
    setLocalTeamId(teamId != null ? String(teamId) : '');
    setLocalPaymentMethodId(paymentMethodId != null ? String(paymentMethodId) : '');
    setLocalShopId(shopId != null ? String(shopId) : '');
    setLocalCourierId(courierId != null ? String(courierId) : '');
    setLocalCountryId(countryId != null ? String(countryId) : '');
    setLocalCourierServiceId(courierServiceId != null ? String(courierServiceId) : '');
  }, [startDate, endDate, teamId, paymentMethodId, shopId, courierId, countryId, courierServiceId]);

  const [shopOptions, setShopOptions] = useState<{ label: string; value: string }[]>([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<{ label: string; value: string }[]>([]);
  const [courierOptions, setCourierOptions] = useState<{ label: string; value: string }[]>([]);
  const [teamOptions, setTeamOptions] = useState<{ label: string; value: string }[]>([]);
  const [countryOptions, setCountryOptions] = useState<{ label: string; value: string }[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [isLoadingCouriers, setIsLoadingCouriers] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  const { data: courierServiceOptions = [], isLoading: isLoadingCourierServices } = useCourierServices(
    localCourierId || ''
  );

  const loadShops = useCallback(async () => {
    try {
      setIsLoadingShops(true);
      const httpClient = createHttpClient(apiClientWH);
      const shopsAdapter = new ShopsApiAdapter(httpClient);
      const shops = await shopsAdapter.getAll();
      setShopOptions([
        { label: t('filters.all'), value: '' },
        ...shops.map((shop) => ({ label: shop.name, value: String(shop.id) })),
      ]);
    } catch {
      setShopOptions([{ label: t('filters.all'), value: '' }]);
    } finally {
      setIsLoadingShops(false);
    }
  }, [t]);

  const loadPaymentMethods = useCallback(async () => {
    try {
      setIsLoadingPaymentMethods(true);
      const httpClient = createHttpClient(apiClientWH);
      const paymentMethodsAdapter = new PaymentMethodsApiAdapter(httpClient);
      const paymentMethods = await paymentMethodsAdapter.getAll();
      setPaymentMethodOptions([
        { label: t('filters.all'), value: '' },
        ...paymentMethods.map((method) => ({ label: method.name, value: String(method.id) })),
      ]);
    } catch {
      setPaymentMethodOptions([{ label: t('filters.all'), value: '' }]);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  }, [t]);

  const loadCouriers = useCallback(async () => {
    try {
      setIsLoadingCouriers(true);
      const httpClient = createHttpClient(apiClientWH);
      const couriersAdapter = new CouriersApiAdapter(httpClient);
      const couriers = await couriersAdapter.getAll();
      setCourierOptions([
        { label: t('filters.all'), value: '' },
        ...couriers.map((courier) => ({ label: courier.name, value: String(courier.id) })),
      ]);
    } catch {
      setCourierOptions([{ label: t('filters.all'), value: '' }]);
    } finally {
      setIsLoadingCouriers(false);
    }
  }, [t]);

  const loadTeams = useCallback(async () => {
    try {
      setIsLoadingTeams(true);
      const httpClient = createHttpClient(apiClientWH);
      const teamsAdapter = new TeamsApiAdapter(httpClient);
      const teams = await teamsAdapter.getTeamsChild();
      setTeamOptions([
        { label: t('filters.all'), value: '' },
        ...teams.map((team) => ({ label: team.name, value: String(team.id) })),
      ]);
    } catch {
      setTeamOptions([{ label: t('filters.all'), value: '' }]);
    } finally {
      setIsLoadingTeams(false);
    }
  }, [t]);

  const loadCountries = useCallback(async () => {
    try {
      setIsLoadingCountries(true);
      const httpClient = createHttpClient(apiClientWH);
      const countriesAdapter = new CountriesApiAdapter(httpClient);
      const countries = await countriesAdapter.getAll();
      setCountryOptions([
        { label: t('filters.all'), value: '' },
        ...countries.map((c) => ({ label: c.name, value: c.id })),
      ]);
    } catch {
      setCountryOptions([{ label: t('filters.all'), value: '' }]);
    } finally {
      setIsLoadingCountries(false);
    }
  }, [t]);

  useEffect(() => {
    loadShops();
    loadPaymentMethods();
    loadCouriers();
    if (showTeamFilter) loadTeams();
    loadCountries();
  }, [loadShops, loadPaymentMethods, loadCouriers, loadTeams, loadCountries, showTeamFilter]);

  const handleDateRangeUpdate = ({ range }: { range: DateRange }) => {
    if (range.from && range.to) {
      const newStartDate = formatDateLocal(range.from);
      const newEndDate = formatDateLocal(range.to);
      setLocalStartDate(newStartDate);
      setLocalEndDate(newEndDate);
      onFiltersChange({
        startDate: newStartDate,
        endDate: newEndDate,
        teamId: localTeamId ? parseInt(localTeamId, 10) : null,
        paymentMethodId: localPaymentMethodId ? parseInt(localPaymentMethodId, 10) : null,
        shopId: localShopId ? parseInt(localShopId, 10) : null,
        courierId: localCourierId ? parseInt(localCourierId, 10) : null,
        countryId: localCountryId ? parseInt(localCountryId, 10) : null,
        courierServiceId: localCourierServiceId ? parseInt(localCourierServiceId, 10) : null,
      });
    }
  };

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onFiltersChange({
      startDate: localStartDate,
      endDate: localEndDate,
      teamId: localTeamId ? parseInt(localTeamId, 10) : null,
      paymentMethodId: localPaymentMethodId ? parseInt(localPaymentMethodId, 10) : null,
      shopId: localShopId ? parseInt(localShopId, 10) : null,
      courierId: localCourierId ? parseInt(localCourierId, 10) : null,
      countryId: localCountryId ? parseInt(localCountryId, 10) : null,
      courierServiceId: localCourierServiceId ? parseInt(localCourierServiceId, 10) : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localTeamId,
    localPaymentMethodId,
    localShopId,
    localCourierId,
    localCountryId,
    localCourierServiceId,
  ]);

  useEffect(() => {
    if (!localCourierId) {
      if (localCourierServiceId) setLocalCourierServiceId('');
    }
  }, [localCourierId, localCourierServiceId]);

  const handleClearFilters = useCallback(() => {
    const defaultStartDate = getDefaultStartDate();
    const defaultEndDate = getDefaultEndDate();
    setLocalStartDate(defaultStartDate);
    setLocalEndDate(defaultEndDate);
    setLocalTeamId('');
    setLocalPaymentMethodId('');
    setLocalShopId('');
    setLocalCourierId('');
    setLocalCountryId('');
    setLocalCourierServiceId('');
    onFiltersChange({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      teamId: null,
      paymentMethodId: null,
      shopId: null,
      courierId: null,
      countryId: null,
      courierServiceId: null,
    });
  }, [getDefaultStartDate, getDefaultEndDate, onFiltersChange]);

  const courierServiceComboboxOptions = [
    { label: t('filters.all'), value: '' },
    ...courierServiceOptions.map((s) => ({
      label: s.name,
      value: s.id,
    })),
  ];

  return (
    <div className="space-y-3 max-h-[min(85vh,640px)] overflow-y-auto overflow-x-hidden pr-1 -mr-1">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1 min-w-0 space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{t('filters.date_range')}</label>
          <DateRangePicker
            initialDateFrom={localStartDate}
            initialDateTo={localEndDate}
            onUpdate={handleDateRangeUpdate}
            align="start"
            showPresets={true}
            triggerClassName="bg-background border-border w-full"
            placeholder={t('filters.select_date_range')}
            dataTestId="logistic-pivot-date-range"
          />
        </div>
        <div className="flex sm:pb-0.5">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={handleClearFilters}
            className="gap-2 w-full lg:w-auto"
            data-testid="logistic-pivot-clear-filters"
          >
            <FontAwesomeIcon icon={faXmark} className="w-3.5 h-3.5" />
            {t('filters.clear')}
          </Button>
        </div>
      </div>

      <Separator className="bg-border" />

      <p className="text-xs font-medium text-muted-foreground">{t('filters.optional_section_label')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {showTeamFilter && (
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">{t('filters.team')}</label>
            <Combobox
              options={teamOptions}
              value={localTeamId}
              setValue={setLocalTeamId}
              placeholder={t('filters.all')}
              dataTestId="logistic-pivot-team"
              disabled={isLoadingTeams}
            />
          </div>
        )}
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">{t('filters.payment_method')}</label>
          <Combobox
            options={paymentMethodOptions}
            value={localPaymentMethodId}
            setValue={setLocalPaymentMethodId}
            placeholder={t('filters.all')}
            dataTestId="logistic-pivot-payment"
            disabled={isLoadingPaymentMethods}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">{t('filters.shop')}</label>
          <Combobox
            options={shopOptions}
            value={localShopId}
            setValue={setLocalShopId}
            placeholder={t('filters.all')}
            dataTestId="logistic-pivot-shop"
            disabled={isLoadingShops}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">{t('filters.courier')}</label>
          <Combobox
            options={courierOptions}
            value={localCourierId}
            setValue={setLocalCourierId}
            placeholder={t('filters.all')}
            dataTestId="logistic-pivot-courier"
            disabled={isLoadingCouriers}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">{t('filters.country')}</label>
          <Combobox
            options={countryOptions}
            value={localCountryId}
            setValue={setLocalCountryId}
            placeholder={t('filters.all')}
            dataTestId="logistic-pivot-country"
            disabled={isLoadingCountries}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">{t('filters.courier_service')}</label>
          <Combobox
            options={courierServiceComboboxOptions}
            value={localCourierServiceId}
            setValue={setLocalCourierServiceId}
            placeholder={t('filters.courier_service_placeholder')}
            dataTestId="logistic-pivot-courier-service"
            disabled={!localCourierId || isLoadingCourierServices}
          />
        </div>
      </div>
    </div>
  );
};
